import type { QueryClient } from "@tanstack/react-query";
import { io, type Socket } from "socket.io-client";

/**
 * Ponte entre o barramento de eventos do servidor e o cache do TanStack Query.
 *
 * O servidor envia **fato**, não estado: `alocacao.deslocada { projetoId,
 * aPartirDe, deltaDias }` em vez das quarenta linhas que o arraste em grupo
 * alterou. Aqui decidimos o que invalidar.
 */

export const chaves = {
  bootstrap: ["bootstrap"] as const,
  alocacoes: ["alocacoes"] as const,
  projetos: ["projetos"] as const,
  projeto: (id: string) => ["projetos", id] as const,
  bloqueios: ["bloqueios"] as const,
  solicitacoes: ["solicitacoes"] as const,
  notificacoes: ["notificacoes"] as const,
  pipeline: ["pipeline"] as const,
  dashboard: ["dashboard"] as const,
  profissionais: ["profissionais"] as const,
  parametros: ["parametros"] as const,
};

type Chave = readonly unknown[];

/**
 * Mapa evento → o que fica obsoleto.
 *
 * Manter isto explícito, e não invalidar tudo a cada evento, é o que impede
 * a grade de recarregar inteira toda vez que qualquer usuário arrasta um slot.
 */
const INVALIDACOES: Record<string, (payload: Record<string, unknown>) => Chave[]> = {
  "alocacao.criada": () => [chaves.alocacoes, chaves.dashboard],
  "alocacao.atualizada": () => [chaves.alocacoes, chaves.dashboard],
  "alocacao.removida": () => [chaves.alocacoes, chaves.dashboard],
  "alocacao.movida": () => [chaves.alocacoes, chaves.dashboard],
  "alocacao.deslocada": (p) => [
    chaves.alocacoes,
    chaves.dashboard,
    chaves.projeto(String(p.projetoId)),
  ],
  "periodo.alterado": () => [chaves.alocacoes, chaves.dashboard],

  "bloqueio.criado": () => [chaves.bloqueios, chaves.alocacoes],
  "bloqueio.decidido": () => [chaves.bloqueios, chaves.alocacoes],

  // O registry precisa do projeto novo: sem ele o seletor de projeto do Flow
  // não o oferece e o rótulo do slot cai no id bruto.
  "projeto.criado": () => [chaves.projetos, chaves.pipeline, chaves.bootstrap],
  "projeto.atualizado": (p) => [
    chaves.projeto(String(p.projetoId)),
    chaves.projetos,
    chaves.pipeline,
    chaves.bootstrap,
  ],
  "projeto.prazo_alterado": (p) => [
    chaves.projeto(String(p.projetoId)),
    chaves.pipeline,
    chaves.dashboard,
  ],
  "projeto.responsavel_alterado": (p) => [chaves.projeto(String(p.projetoId)), chaves.pipeline],
  "projeto.estagio_movido": (p) => [
    chaves.projeto(String(p.projetoId)),
    chaves.pipeline,
    chaves.bootstrap,
  ],
  "projeto.pausado": () => [chaves.pipeline, chaves.alocacoes, chaves.bootstrap],
  "projeto.retomado": () => [chaves.pipeline, chaves.alocacoes, chaves.bootstrap],
  "fase.status_alterado": (p) => [chaves.projeto(String(p.projetoId)), chaves.pipeline],

  "cliente.criado": () => [chaves.bootstrap],

  "profissional.criado": () => [chaves.bootstrap, chaves.profissionais],
  "profissional.atualizado": () => [chaves.bootstrap, chaves.profissionais],
  "profissional.inatividade_alterada": () => [chaves.bootstrap, chaves.alocacoes],

  "anexo.adicionado": (p) => [chaves.projeto(String(p.projetoId)), chaves.notificacoes],
  "briefing.lido": (p) => [chaves.projeto(String(p.projetoId))],
  "realizado.fora_da_alocacao": (p) => [chaves.projeto(String(p.projetoId))],
  "realizado.ajustado": (p) => [chaves.projeto(String(p.projetoId))],
  "debriefing.publicado": (p) => [chaves.projeto(String(p.projetoId))],
  "debriefing.insight_aprovado": (p) => [chaves.projeto(String(p.projetoId))],

  "solicitacao.criada": () => [chaves.solicitacoes],
  "solicitacao.aguardando_aceite": () => [chaves.solicitacoes],
  "solicitacao.decidida": () => [chaves.solicitacoes],
  "solicitacao.aceite_respondido": () => [chaves.solicitacoes, chaves.alocacoes],

  // Só chega a quem é destinatário — o servidor entrega na sala do usuário.
  "notificacao.criada": () => [chaves.notificacoes],

  "parametros.versionados": () => [chaves.bootstrap, chaves.parametros],

  "usuario.convidado": () => [chaves.bootstrap],
  "usuario.ativado": () => [chaves.bootstrap],
};

interface EventoRecebido {
  nome: string;
  payload: Record<string, unknown>;
  usuarioId: string | null;
  ts: string;
}

let socket: Socket | null = null;

export function conectarEventos(queryClient: QueryClient, auth?: Record<string, string>) {
  if (socket?.connected) return socket;

  socket = io("/eventos", {
    path: "/socket.io",
    transports: ["websocket"],
    withCredentials: true,
    auth: auth ?? {},
  });

  socket.on("evento", (evento: EventoRecebido) => {
    const alvos = INVALIDACOES[evento.nome]?.(evento.payload);
    if (!alvos) return;
    for (const queryKey of alvos) {
      void queryClient.invalidateQueries({ queryKey });
    }
  });

  return socket;
}

export function desconectarEventos() {
  socket?.disconnect();
  socket = null;
}
